import { createContext, type ReactNode, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { UseFilePickerOptions } from "@medbillai/ui";
import type { DocumentScanEntryPoint } from "@medbillai/utils/documentScan";
import type { UploadFunnelAttempt } from "../../lib/analytics/uploadFunnel";
import { useScreenLock } from "../../lib/screen-lock";
import type { UploadError, UploadFile } from "../../provider/documents";
import { UploadDocumentSheet } from "./UploadDocumentSheet";
type PickerRequest = {
  title?: string;
  /** Overrides the sheet's "Upload a file" picker constraints (accept, multiple). */
  filePickerOptions?: Pick<UseFilePickerOptions, "accept" | "multiple">;
  onFilesLoaded: (files: UploadFile[], funnelAttempt?: UploadFunnelAttempt) => Promise<void>;
  onUploadError: (error: UploadError) => void;
};
type OpenDocumentPicker = (request: PickerRequest) => void;
const UploadDocumentPickerContext = createContext<OpenDocumentPicker | null>(null);

// Surfaces the provider's funnel envelope to uploads that never open the sheet
// (drag-and-drop) so their attempts carry the same provenance — entry point and
// the case they answer — as picked ones. "OTHER" outside a provider — honest,
// and excluded from surface-scoped cuts.
const UploadFunnelContext = createContext<{
  entryPoint: DocumentScanEntryPoint;
  issueId?: string;
}>({
  entryPoint: "OTHER"
});
export const useUploadFunnelContext = () => useContext(UploadFunnelContext);
export const useUploadDocumentPicker = () => useContext(UploadDocumentPickerContext);
export const useRequiredUploadDocumentPicker = () => {
  const openDocumentPicker = useUploadDocumentPicker();
  if (!openDocumentPicker) {
    throw new Error("useRequiredUploadDocumentPicker must be used within UploadDocumentPickerProvider");
  }
  return openDocumentPicker;
};
export const UploadDocumentPickerProvider = ({
  children,
  issueId,
  entryPoint
}: {
  children: ReactNode;
  issueId?: string;
  entryPoint: DocumentScanEntryPoint;
}) => {
  const funnelContext = useMemo(() => ({
    entryPoint,
    ...(issueId !== undefined ? {
      issueId
    } : {})
  }), [entryPoint, issueId]);
  const [request, setRequest] = useState<PickerRequest | null>(null);
  // Increments on every open so web can re-fire the OS picker after a failed
  // or unanswered attempt even when `open` never dropped to false.
  const [pickerGeneration, setPickerGeneration] = useState(0);
  // Capture callbacks for the in-flight pick. Web has no sheet to keep mounted
  // across async load failures, so reading `request` from render is racy.
  const requestRef = useRef<PickerRequest | null>(null);
  const {
    disableScreenLock,
    enableScreenLock
  } = useScreenLock();
  const openDocumentPicker = useCallback<OpenDocumentPicker>(nextRequest => {
    requestRef.current = nextRequest;
    setRequest(nextRequest);
    setPickerGeneration(generation => generation + 1);
  }, []);
  return <UploadDocumentPickerContext.Provider value={openDocumentPicker} data-sentry-element="UploadDocumentPickerContext.Provider" data-sentry-component="UploadDocumentPickerProvider" data-sentry-source-file="UploadDocumentPickerProvider.tsx">
      <UploadFunnelContext.Provider value={funnelContext} data-sentry-element="UploadFunnelContext.Provider" data-sentry-source-file="UploadDocumentPickerProvider.tsx">
        {children}
      </UploadFunnelContext.Provider>
      <UploadDocumentSheet open={request !== null} pickerGeneration={pickerGeneration} onOpenChange={open => {
      if (!open) {
        requestRef.current = null;
        setRequest(null);
      }
    }} onCameraPermissionCheckStart={disableScreenLock} onCameraPermissionCheckComplete={enableScreenLock} onUploadError={error => requestRef.current?.onUploadError(error)} onFilesLoaded={async (files, funnelAttempt) => {
      const activeRequest = requestRef.current;
      if (!activeRequest) {
        return;
      }
      requestRef.current = null;
      setRequest(null);
      try {
        await activeRequest.onFilesLoaded(files, funnelAttempt);
      } catch (error) {
        activeRequest.onUploadError({
          error: error instanceof Error ? error : new Error(String(error))
        });
      }
    }} title={request?.title} filePickerOptions={request?.filePickerOptions} issueId={issueId} entryPoint={entryPoint} data-sentry-element="UploadDocumentSheet" data-sentry-source-file="UploadDocumentPickerProvider.tsx" />
    </UploadDocumentPickerContext.Provider>;
};